// eslint-disable-next-line import/no-unresolved
import { TextEditor } from 'handsontable/editors/textEditor';

import { NEGOTIATION_STARTING_PRICE_COLUMN_KEY } from '@components/bid_request/details/constants';

import {
  computeDiscountPercent,
  computePriceFromDiscountPercent,
  parseEditorInput,
} from 'features/suggested-price-column/utils/discount-from-nsp';

import styles from './suggested-price-split-editor.module.scss';

const FALLBACK_PRECISION = 2;

export interface SuggestedPriceSplitEditorOptions {
  /** Resolved symbol for the event's currency, shown as the price field's adornment. */
  currencySymbol: string;
  /** Translated accessible name for the discount-from-NSP field. */
  percentLabel: string;
  /** Translated accessible name for the price field. */
  priceLabel: string;
}

interface NumericFormatCellProperty {
  maximumFractionDigits?: number;
}

const editorClassCache = new Map<string, typeof TextEditor>();

/**
 * Cache key for a set of editor options.
 *
 * EVERY option that the class bakes in must appear here. The currency symbol in particular: it is
 * captured in the closure and written straight into the DOM, so omitting it would let a USD grid
 * and a EUR grid share one cached class and render the wrong symbol on whichever mounted second.
 *
 * `JSON.stringify` rather than concatenation: two values joined by a separator can collide with a
 * different set that happens to contain that separator, and every printable character is legal in
 * translated text. Stringifying an array escapes the boundary, so distinct option sets cannot
 * produce the same key.
 */
const buildCacheKey = (
  { currencySymbol, percentLabel, priceLabel }: SuggestedPriceSplitEditorOptions,
): string => JSON.stringify([currencySymbol, priceLabel, percentLabel]);

/**
 * Builds the split-cell ($ / %) editor class for the Suggested Price column.
 *
 * Both fields are two views of one value. Typing in either recomputes the other against the row's
 * Negotiation Starting Price, and only the dollar amount is ever committed — the percentage is
 * derived for display and never stored, so the existing override-save path is untouched.
 *
 * The percent side is a DISCOUNT off NSP, matching the `TrendIndicator` the display renderer shows
 * under the cell. A price above NSP is a premium and reads as a negative discount.
 *
 * `max_discount` is deliberately not applied: it bounds AI-generated predictive prices, not manual
 * overrides. The only bound is that the price cannot go below zero.
 *
 * ## Why this is a native Handsontable editor rather than a React one
 *
 * `@handsontable/react-wrapper` ships `useHotEditor` / `EditorComponent` for writing editors as
 * React components, which would be the obvious choice. It is deliberately NOT used, for two
 * reasons that both bite on the version we are upgrading toward:
 *
 *  1. The React wrapper mounts component editors in a portal on `document.body`. Handsontable 18.1
 *     added `#isPathWithinGrid(eventPath)`, which treats a click whose composed path contains
 *     neither `rootElement` nor `rootPortalElement` as an outside click and deselects — closing the
 *     editor before the value can commit. A body portal is in neither, so React editors break on
 *     18.1.x. Native editors mount inside `rootElement` (`TextEditor#createElements` does
 *     `this.hot.rootElement.appendChild(...)`), so this editor works on 18.0.x and 18.1.x alike.
 *  2. `useHotEditor` wraps its value in `useDeferredValue`, which makes two controlled inputs drop
 *     characters and jump the caret under fast typing.
 *
 * Upstream fixes both in handsontable/handsontable#13376 — it mounts the editor host inside
 * `rootPortalElement` and drops `useDeferredValue`. That merged to `develop` on 2026-09-04 but is
 * in NO published release as of 18.1.1, verified by diffing the published wrapper bundles.
 *
 * ## What to delete once #13376 ships in a release
 *
 * Rewriting this as a `useHotEditor` component becomes viable then, and would let the fields use
 * ADS inputs and `useTranslation` directly. It is optional — this editor keeps working either way.
 * If it is rewritten, these exist ONLY to avoid the React path and can go:
 *   - this factory and `editorClassCache` (a React component is already a stable module reference)
 *   - the `options` argument, plumbed from `generateTableConfiguration` (use `useTranslation` for
 *     the labels and read the currency from props/context for the symbol)
 *   - `resolveNegotiationStartingPrice`, which reads NSP out of the grid (use `useLotData(lotId)`)
 *   - the `hotEditor` routing in `hot-line-items-table.tsx`, if no other column uses a native editor
 * Keep `discount-from-nsp.ts` and this file's tests either way — the maths and behaviour do not change.
 *
 * ## Why the returned class is cached, and why on the labels
 *
 * The cache exists to keep the returned class IDENTITY stable across renders. Handsontable stores
 * one editor instance per grid, looked up by class identity in a `WeakMap` (`editors/registry`),
 * and a class it has not seen before registers an `afterDestroy` handler on the process-global hook
 * bucket that is never removed. `buyerInitialColumnSettings` re-runs on many renders, so returning
 * a fresh class each call would leak a closure per render and defeat the instance pooling.
 *
 * The key is the OPTION TEXT — two translated aria-labels and the event's currency symbol. It
 * carries no price, no percentage, and no line-item data. Cell values never reach this function;
 * one class serves the entire column, and Handsontable moves its single instance between cells as
 * the selection changes. Two rows with identical prices are not distinguishable here and do not
 * need to be.
 *
 * Options change only with locale or event currency, so this holds one class per locale/currency
 * pair — bounded by those, not by rows, renders, or grids.
 */
export const createSuggestedPriceSplitEditor = (
  options: SuggestedPriceSplitEditorOptions,
): typeof TextEditor => {
  const cacheKey = buildCacheKey(options);
  const cached = editorClassCache.get(cacheKey);

  if (cached) return cached;

  class SuggestedPriceSplitEditor extends TextEditor {
    private container!: HTMLDivElement;

    private priceInput!: HTMLInputElement;

    private percentInput!: HTMLInputElement;

    private isSyncing = false;

    private seededText = '';

    static get EDITOR_TYPE (): string {
      return 'suggestedPriceSplit';
    }

    /**
     * Precision for BOTH fields, taken from the column's configured `award_price_precision`.
     *
     * The percent side intentionally shares it, so a user may type decimals into the percent field
     * to the same accuracy the price allows. The fallback matches the inline default in
     * `LineItemsTable.tsx` and is only reached if the column arrives without a `numericFormat`,
     * which `createNumericFormat` always supplies today.
     */
    private get precision (): number {
      const numericFormat = this.cellProperties?.numericFormat as NumericFormatCellProperty | undefined;

      return numericFormat?.maximumFractionDigits ?? FALLBACK_PRECISION;
    }

    /**
     * The row's Negotiation Starting Price, read out of the grid rather than the React tree,
     * because a native editor has no access to `useLotData`.
     *
     * Returns `null` when NSP cannot be resolved — the column is absent in this flag combination,
     * the row has no value, or the editor has not been prepared onto a row yet (`BaseEditor#row`
     * is only populated from `prepare()` onward). A null result disables the percent field rather
     * than showing a percentage computed against a guess.
     */
    private resolveNegotiationStartingPrice (): number | null {
      const { columns } = this.hot.getSettings();

      if (!Array.isArray(columns)) return null;

      const nspIndex = columns.findIndex(
        column => (column as { id?: string })?.id === NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
      );

      if (nspIndex < 0) return null;
      if (typeof this.row !== 'number') return null;

      const raw = this.hot.getDataAtCell(this.row, nspIndex);

      if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
      if (typeof raw === 'string') return parseEditorInput(raw);

      return null;
    }

    /**
     * Builds the two fields inside the holder `TextEditor` already positions over the cell.
     *
     * The inherited TEXTAREA stays in the DOM because Handsontable's own lifecycle still touches it
     * (IME support, `prepare`'s allowInvalid reset), but it is never the visible field —
     * `getValue`, `setValue` and `focus` all target `priceInput` instead.
     *
     * Listeners are wired through arrow closures calling prototype methods, NOT as `this.handleX`
     * class-field arrows. `BaseEditor`'s constructor calls `this.init()`, which reaches this method
     * while still inside `super()` — before any derived class field initialiser has run. A
     * class-field arrow is still `undefined` at that point, so `addEventListener` would silently
     * bind nothing and neither field would ever sync.
     */
    createElements (): void {
      super.createElements();

      const { rootDocument } = this.hot;

      this.container = rootDocument.createElement('div');
      this.container.className = styles.container;

      const priceAdornment = rootDocument.createElement('span');
      priceAdornment.className = styles.adornment;
      priceAdornment.textContent = options.currencySymbol;
      priceAdornment.setAttribute('aria-hidden', 'true');

      this.priceInput = rootDocument.createElement('input');
      this.priceInput.className = `${styles.field} ${styles.priceField}`;
      this.priceInput.setAttribute('type', 'text');
      this.priceInput.setAttribute('inputmode', 'decimal');
      this.priceInput.setAttribute('aria-label', options.priceLabel);
      this.priceInput.setAttribute('data-testid', 'suggested-price-split-editor-price');

      this.percentInput = rootDocument.createElement('input');
      this.percentInput.className = `${styles.field} ${styles.percentField}`;
      this.percentInput.setAttribute('type', 'text');
      this.percentInput.setAttribute('inputmode', 'decimal');
      this.percentInput.setAttribute('aria-label', options.percentLabel);
      this.percentInput.setAttribute('data-testid', 'suggested-price-split-editor-percent');

      const percentAdornment = rootDocument.createElement('span');
      percentAdornment.className = styles.adornment;
      percentAdornment.textContent = '%';
      percentAdornment.setAttribute('aria-hidden', 'true');

      this.container.appendChild(priceAdornment);
      this.container.appendChild(this.priceInput);
      this.container.appendChild(this.percentInput);
      this.container.appendChild(percentAdornment);
      this.TEXTAREA_PARENT.appendChild(this.container);

      this.TEXTAREA.setAttribute('aria-hidden', 'true');
      this.TEXTAREA.setAttribute('tabindex', '-1');
      this.TEXTAREA.style.display = 'none';

      this.priceInput.addEventListener('input', () => this.handlePriceInput());
      this.percentInput.addEventListener('input', () => this.handlePercentInput());
      this.priceInput.addEventListener('keydown', event => this.handlePriceKeyDown(event));
    }

    /**
     * Dollar edit drives the percent field.
     *
     * `isSyncing` guards the two-way sync: writing the derived value into the sibling field fires
     * that field's own `input` event, which would recompute back into the field being typed in and
     * fight the caret. It is set only while a programmatic write is in flight.
     */
    private handlePriceInput (): void {
      if (this.isSyncing) return;

      const nsp = this.resolveNegotiationStartingPrice();
      const price = parseEditorInput(this.priceInput.value);
      const percent = computeDiscountPercent(nsp, price, this.precision);

      this.isSyncing = true;
      this.percentInput.value = percent === null ? '' : String(percent);
      this.isSyncing = false;
    }

    /**
     * Discount edit drives the price field, which is the value that actually commits.
     *
     * A non-derivable discount (blank field, a lone `-` mid-keystroke while typing a premium)
     * leaves the price field untouched rather than blanking it. Wiping it would commit an empty
     * value, which the change payload resolves to a real $0 override — silently replacing the
     * buyer's price with zero. Clearing the price is done by clearing the price field itself.
     */
    private handlePercentInput (): void {
      if (this.isSyncing) return;

      const nsp = this.resolveNegotiationStartingPrice();
      const percent = parseEditorInput(this.percentInput.value);
      const price = computePriceFromDiscountPercent(nsp, percent, this.precision);

      if (price === null) return;

      this.isSyncing = true;
      this.priceInput.value = String(price);
      this.isSyncing = false;
    }

    /**
     * Makes Tab move between the two fields before it means "leave the cell".
     *
     * Without this, Handsontable's grid-level Tab handler fires on the first press and commits out
     * of the cell, leaving the percent field unreachable by keyboard. Tab from the percent field is
     * left alone so it still advances to the next cell.
     */
    private handlePriceKeyDown (event: KeyboardEvent): void {
      if (event.key !== 'Tab' || event.shiftKey || this.percentInput.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.percentInput.focus();
      this.percentInput.select();
    }

    /**
     * Seeds the discount field from the dollar field and disables it when NSP is unresolvable.
     *
     * A disabled percent field is the deliberate answer to "NSP should never be null, but handle
     * it": a discount relative to an unknown baseline is worse than no discount. An NSP of exactly
     * 0 is NOT this case — it resolves to 0% and the field stays editable.
     */
    private syncFromPrice (): void {
      const nsp = this.resolveNegotiationStartingPrice();
      const price = parseEditorInput(this.priceInput.value);
      const percent = computeDiscountPercent(nsp, price, this.precision);

      this.percentInput.disabled = nsp === null;
      this.percentInput.value = percent === null ? '' : String(percent);
    }

    /**
     * Returns the ORIGINAL cell value verbatim when the field still holds exactly what was seeded.
     *
     * The field is seeded at display precision, so its text routinely differs from the stored value
     * without anyone editing anything: `318.469` seeds as `318.4690` at 4dp, and `125.456` seeds as
     * `125.46` at 2dp. `getLineItemChangesPayload` compares old and new as strings, so returning the
     * reformatted text would record a change and convert an AI predictive price into a real buyer
     * override — flipping the reset icon on and pulling the row out of regen — purely from opening
     * a cell.
     *
     * The comparison is on the seeded TEXT rather than the parsed number on purpose. Comparing
     * numbers rounded to display precision would also swallow a real edit that happens to round to
     * the same thing (typing `125.4567` into a 2dp column), silently discarding what the user
     * typed. Untouched text is the only thing that reliably means "the user changed nothing".
     */
    getValue (): string {
      if (this.priceInput.value === this.seededText) {
        return this.originalValue === null || this.originalValue === undefined
          ? ''
          : String(this.originalValue);
      }

      return this.priceInput.value;
    }

    /**
     * Seeds the price field at the column's display precision, so the editor shows the same digits
     * the cell showed a moment earlier.
     *
     * The renderer pads through `displayCurrencyWithPrecision`, so a stored `318.469` reads as
     * `318.4690` at 4dp. Seeding the raw value would drop that trailing digit the instant the
     * editor opens, which looks like the grid silently altering the number. `getValue` above is
     * what makes this safe to do — it hands back the original when nothing changed numerically, so
     * reformatting here cannot leak into a saved override.
     */
    setValue (value?: unknown): void {
      const parsed = parseEditorInput(
        value === null || value === undefined ? '' : String(value),
      );

      this.priceInput.value = parsed === null ? '' : parsed.toFixed(this.precision);
      this.seededText = this.priceInput.value;
      this.syncFromPrice();
    }

    open (): void {
      super.open();
      this.syncFromPrice();
    }

    /** Focuses the visible dollar field rather than the hidden TEXTAREA `super.focus()` targets. */
    focus (): void {
      this.priceInput.focus();
      this.priceInput.select();
    }

    /**
     * Sizes the split container to the edited cell.
     *
     * `super.refreshDimensions()` positions the holder and sizes the TEXTAREA via `autoResize`.
     * Since the TEXTAREA is hidden here it contributes no width, so the container is sized from the
     * same cell rect to fill the cell exactly.
     */
    refreshDimensions (force = false): void {
      super.refreshDimensions(force);

      const cellRect = this.getEditedCellRect();

      if (!cellRect || !this.container) return;

      this.container.style.width = `${Math.min(cellRect.width, cellRect.maxWidth)}px`;
      this.container.style.height = `${Math.min(cellRect.height, cellRect.maxHeight)}px`;
    }
  }

  editorClassCache.set(cacheKey, SuggestedPriceSplitEditor);

  return SuggestedPriceSplitEditor;
};
